/**
 * FP3-069 — Direct Solo Provider Pilot signup page.
 *
 *   /start-pilot
 *
 * Public route. Calls POST /api/auth/signup-solo-pilot which
 * creates the user + organization + provider + Primary Workspace
 * school + active schedule period in a single transaction and logs
 * the user in via cookies (and returns access/refresh tokens for
 * any client that prefers explicit tokens).
 *
 * No SuperAdmin approval gate. No billing / Stripe behavior. The
 * school / district request-access path (/request-access) stays
 * untouched.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle,
  Sparkle,
  ArrowRight,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function StartPilot() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
    organization_name: "",
    accepted_terms: false,
  });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const passwordTooShort = form.password.length > 0 && form.password.length < 8;
  const passwordMismatch =
    form.confirm_password.length > 0 && form.password !== form.confirm_password;
  const canSubmit =
    !!form.first_name.trim() &&
    !!form.last_name.trim() &&
    !!form.email.trim() &&
    form.password.length >= 8 &&
    form.password === form.confirm_password &&
    form.accepted_terms &&
    !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.signupSoloPilot({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        organization_name: form.organization_name.trim(),
        accepted_terms: true,
      });
      toast.success("Welcome to Frame Practice! Your Pilot workspace is ready.");
      // Hydrate the AuthProvider from the auth cookies set by the
      // signup endpoint, then land on Today.
      try {
        await refresh();
      } catch {
        /* noop — the route guard will refresh on mount */
      }
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        (typeof detail === "string" && detail) ||
          "Could not start your Pilot workspace. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="start-pilot-page"
      className="min-h-screen bg-[#F7F5EF] body-font"
    >
      <div className="mx-auto max-w-2xl px-5 sm:px-8 py-10 lg:py-14">
        <div className="mb-7">
          <Brand />
        </div>

        <Card
          data-testid="start-pilot-card"
          className="border-[#E5E3DB] shadow-[0_2px_14px_rgba(31,41,55,0.04)] rounded-2xl"
        >
          <CardContent className="p-7 sm:p-9">
            <div className="mb-6">
              <div
                data-testid="start-pilot-pilot-pill"
                className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] text-[#1E3F33] bg-[#E8F0EB] rounded-full px-2.5 py-1 mb-3 font-semibold"
              >
                <Sparkle size={11} weight="duotone" />
                Solo provider · Pilot
              </div>
              <h1 className="text-[24px] font-semibold text-[#1F2937] heading-font leading-tight">
                Start your Pilot
              </h1>
              <p className="text-[13px] text-[#4B5563] mt-2 leading-relaxed max-w-lg">
                Create your own solo workspace in under a minute. No
                approval, no billing, no credit card. You&rsquo;ll land
                straight in your dashboard with an active Pilot period.
              </p>
            </div>

            <form
              data-testid="start-pilot-form"
              onSubmit={onSubmit}
              className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4"
            >
              <FieldShell label="First name" required>
                <Input
                  data-testid="start-pilot-first-name"
                  value={form.first_name}
                  onChange={(e) => upd("first_name", e.target.value)}
                  required
                  className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                />
              </FieldShell>
              <FieldShell label="Last name" required>
                <Input
                  data-testid="start-pilot-last-name"
                  value={form.last_name}
                  onChange={(e) => upd("last_name", e.target.value)}
                  required
                  className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                />
              </FieldShell>
              <FieldShell label="Email" required colSpan2>
                <Input
                  data-testid="start-pilot-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => upd("email", e.target.value)}
                  required
                  placeholder="name@email.com"
                  className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                />
              </FieldShell>
              <FieldShell
                label="Workspace name · optional"
                helper="Leave blank and we'll name it after you."
                helperTestId="start-pilot-org-helper"
                colSpan2
              >
                <Input
                  data-testid="start-pilot-org-name"
                  value={form.organization_name}
                  onChange={(e) => upd("organization_name", e.target.value)}
                  placeholder="e.g. Jordan Lee Practice"
                  className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                />
              </FieldShell>
              <FieldShell
                label="Password"
                required
                helper={
                  passwordTooShort
                    ? "Password must be at least 8 characters."
                    : "At least 8 characters."
                }
                helperTestId="start-pilot-pw-helper"
              >
                <Input
                  data-testid="start-pilot-password"
                  type="password"
                  value={form.password}
                  onChange={(e) => upd("password", e.target.value)}
                  required
                  minLength={8}
                  className={`h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33] ${
                    passwordTooShort ? "border-[#A8453A]" : ""
                  }`}
                />
              </FieldShell>
              <FieldShell
                label="Confirm password"
                required
                helper={passwordMismatch ? "Passwords do not match." : ""}
                helperTestId="start-pilot-pw-confirm-helper"
              >
                <Input
                  data-testid="start-pilot-password-confirm"
                  type="password"
                  value={form.confirm_password}
                  onChange={(e) => upd("confirm_password", e.target.value)}
                  required
                  minLength={8}
                  className={`h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33] ${
                    passwordMismatch ? "border-[#A8453A]" : ""
                  }`}
                />
              </FieldShell>

              <div className="sm:col-span-2 flex items-start gap-2 mt-1">
                <input
                  id="start-pilot-terms"
                  data-testid="start-pilot-accept-terms"
                  type="checkbox"
                  checked={form.accepted_terms}
                  onChange={(e) => upd("accepted_terms", e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-[#9AA0A6] text-[#1E3F33] focus:ring-[#1E3F33]/30"
                  required
                />
                <label
                  htmlFor="start-pilot-terms"
                  className="text-[12px] text-[#4B5563] leading-relaxed select-none"
                >
                  I agree to the Frame Practice{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="start-pilot-terms-link"
                    className="text-[#1E3F33] font-medium underline decoration-[#8FA998]/60 underline-offset-2 hover:decoration-[#1E3F33]"
                  >
                    Terms of Use
                  </Link>
                  {" "}and{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="start-pilot-privacy-link"
                    className="text-[#1E3F33] font-medium underline decoration-[#8FA998]/60 underline-offset-2 hover:decoration-[#1E3F33]"
                  >
                    Privacy Policy
                  </Link>
                  .
                </label>
              </div>

              <div className="sm:col-span-2 flex items-center justify-between mt-3 pt-3 border-t border-[#F1F2F4]">
                <Link
                  to="/login"
                  data-testid="start-pilot-back-to-login"
                  className="text-[12.5px] text-[#1E3F33] font-medium hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors"
                >
                  ← Already have an account? Sign in
                </Link>
                <Button
                  type="submit"
                  data-testid="start-pilot-submit"
                  disabled={!canSubmit}
                  className="h-10 px-5 bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white rounded-lg text-sm font-medium tracking-tight transition-colors"
                >
                  {submitting ? "Creating workspace…" : (
                    <span className="inline-flex items-center gap-1.5">
                      Start Pilot
                      <ArrowRight size={14} weight="bold" />
                    </span>
                  )}
                </Button>
              </div>
            </form>

            <ul className="mt-7 pt-5 border-t border-[#F1F2F4] grid grid-cols-1 sm:grid-cols-3 gap-4 text-[11.5px] text-[#4B5563]">
              <Bullet>Solo workspace created instantly</Bullet>
              <Bullet>Invite assistants when you&rsquo;re ready</Bullet>
              <Bullet>No billing during Pilot</Bullet>
            </ul>
          </CardContent>
        </Card>

        <p className="text-center text-[12px] text-[#6B7280] mt-5">
          Looking for school or district access instead?{" "}
          <Link
            to="/request-access"
            data-testid="start-pilot-to-request-access"
            className="text-[#1E3F33] font-medium hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors"
          >
            Request team access
          </Link>
          .
        </p>

        <p className="text-center text-[11px] text-[#9AA0A6] mt-2">
          © Frame Practice · Solo Provider Pilot
        </p>
        <p
          data-testid="start-pilot-contact-footer"
          className="text-center text-[11px] text-[#9AA0A6] mt-1.5"
        >
          Questions?{" "}
          <Link
            to="/contact"
            data-testid="start-pilot-contact-link"
            className="text-[#1E3F33] font-medium hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors"
          >
            Contact support
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-1.5">
      <CheckCircle
        size={14}
        weight="duotone"
        className="text-[#1E3F33] flex-shrink-0 mt-[1px]"
      />
      <span className="leading-relaxed">{children}</span>
    </li>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl bg-[#1E3F33] flex items-center justify-center shadow-sm">
        <span className="text-white text-base font-bold heading-font">F</span>
      </div>
      <div className="leading-tight">
        <div className="text-[19px] font-bold text-[#1E3F33] heading-font tracking-tight">
          Frame
          <span className="text-[#8FA998] font-medium ml-0.5">Practice</span>
        </div>
        <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] mt-0.5">
          Provider workspace
        </div>
      </div>
    </div>
  );
}

function FieldShell({ label, required, colSpan2, helper, helperTestId, children }) {
  return (
    <div className={colSpan2 ? "sm:col-span-2" : ""}>
      <Label className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
        {label}
        {required && <span className="text-[#9C6B2E] ml-0.5">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {helper && (
        <p
          data-testid={helperTestId}
          className="mt-1.5 text-[11px] text-[#6B7280] leading-snug"
        >
          {helper}
        </p>
      )}
    </div>
  );
}
